// ============================================================================
// InlineTextEditor — TipTap rich text editor for CMS Page Builder (T17)
// ============================================================================

import { useCallback, useMemo, useRef, useState } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/react';
import { FontFamily } from '../extensions/FontFamilyExtension';
import { Box, HStack, IconButton, Tooltip } from '@chakra-ui/react';
import type { ProseMirrorDoc } from '../types';
import { COLOR_TOKENS } from '../constants/colorTokens';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface InlineTextEditorProps {
  /** ProseMirror JSON content for text blocks */
  content: ProseMirrorDoc;
  /** Called when content changes — outputs ProseMirror JSON */
  onChange: (content: ProseMirrorDoc) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** If true, single-line mode (Enter blurs, no paragraph breaks) — for headings */
  singleLine?: boolean;
  /** Heading level for single-line mode (renders as h1-h4) */
  headingLevel?: 1 | 2 | 3 | 4;
  /** Called when editor blurs (user clicks away) */
  onBlur?: () => void;
  /** Auto-focus on mount */
  autoFocus?: boolean;
}

// ---------------------------------------------------------------------------
// Default empty doc
// ---------------------------------------------------------------------------
const EMPTY_DOC: ProseMirrorDoc = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

// ---------------------------------------------------------------------------
// Single-line extension — prevents Enter from creating new paragraphs
// ---------------------------------------------------------------------------
const SingleLineExtension = Extension.create({
  name: 'singleLine',

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        editor.commands.blur();
        return true;
      },
      'Shift-Enter': ({ editor }) => {
        editor.commands.blur();
        return true;
      },
    };
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function InlineTextEditor({
  content,
  onChange,
  placeholder,
  singleLine = false,
  headingLevel: _headingLevel,
  onBlur,
  autoFocus = false,
}: InlineTextEditorProps): React.ReactElement {
  const [isFocused, setIsFocused] = useState(false);

  const handleUpdate = useCallback(
    ({ editor }: { editor: { getJSON: () => unknown } }) => {
      const json = editor.getJSON() as ProseMirrorDoc;
      onChange(json);
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: singleLine ? false : { levels: [1, 2, 3, 4] },
      link: { openOnClick: false },
    }),
    TextStyle,
    Color,
    FontFamily,
    ...(singleLine ? [SingleLineExtension] : []),
  ], [singleLine]);

  const editor = useEditor({
    extensions,
    content: content ?? EMPTY_DOC,
    onUpdate: handleUpdate,
    onBlur: handleBlur,
    onFocus: handleFocus,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        style: [
          'outline: none;',
          'cursor: text;',
          'min-height: 1em;',
          'width: 100%;',
          ...(placeholder
            ? [`--placeholder: "${placeholder.replace(/"/g, '\\"')}";`]
            : []),
        ].join(' '),
      },
    },
  });

  return (
    <Box
      gc-testing-id="inlineTextEditor"
      position="relative"
      w="100%"
      cursor="text"
      bg="transparent"
      borderRadius="sm"
      transition="box-shadow 0.15s ease"
      boxShadow={isFocused ? '0 0 0 1px rgba(255, 107, 0, 0.4)' : 'none'}
      sx={{
        '.tiptap': {
          outline: 'none',
          bg: 'transparent',
          minH: '1em',
          w: '100%',
          p: 0,
          'p, h1, h2, h3, h4': { m: 0, mb: '8px' },
          'p:last-child, h1:last-child, h2:last-child, h3:last-child, h4:last-child': { mb: 0 },
          'em': { fontStyle: 'italic' },
          'strong': { fontWeight: 800 },
          'a': {
            color: 'var(--chakra-colors-blaze-blaze)',
            textDecoration: 'underline',
            cursor: 'text',
            pointerEvents: 'none',
          },
          'span[style*="color"]': {
            // Allow TipTap inline color styles to render
          },
          'p.is-editor-empty:first-of-type::before': placeholder
            ? {
                content: `"${placeholder.replace(/"/g, '\\"')}"`,
                color: 'whiteAlpha.400',
                float: 'left',
                height: 0,
                pointerEvents: 'none',
              }
            : undefined,
        },
      }}
    >
      {editor && (
        <BubbleMenu
          editor={editor}
          options={{ placement: 'top', offset: 8 }}
        >
          <BubbleToolbar editor={editor} singleLine={singleLine} />
        </BubbleMenu>
      )}
      <EditorContent editor={editor} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BubbleToolbar — reactive mark state via useEditorState
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Color tokens — canonical design-system palette (shared with section/block text color)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ColorPicker — uses mouseDown preventDefault to keep editor selection alive
// ---------------------------------------------------------------------------
function ColorPicker({ editor, activeColor }: { editor: Editor; activeColor?: string }): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Box position="relative" ref={containerRef}>
      <Tooltip label="Text Color" placement="top" hasArrow>
        <IconButton
          gc-testing-id="bubbleMenuColor"
          aria-label="Text Color"
          icon={<ColorIcon color={activeColor} />}
          size="xs"
          variant={activeColor ? 'solid' : 'ghost'}
          colorScheme={activeColor ? 'orange' : 'whiteAlpha'}
          color="white"
          onMouseDown={(e) => {
            e.preventDefault(); // prevent editor blur
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        />
      </Tooltip>
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left="50%"
          transform="translateX(-50%)"
          mt={1}
          bg="gray.800"
          border="1px solid"
          borderColor="gray.600"
          borderRadius="md"
          boxShadow="lg"
          p={1.5}
          zIndex={10}
          display="grid"
          gridTemplateColumns="repeat(4, 1fr)"
          gap={1}
          onMouseDown={(e) => e.preventDefault()} // prevent blur on panel click
        >
          {COLOR_TOKENS.map((token) => (
            <Tooltip key={token.id} label={token.label} placement="bottom" hasArrow openDelay={300}>
              <Box
                gc-testing-id={`bubbleMenuColor-${token.id}`}
                as="button"
                w="22px"
                h="22px"
                borderRadius="full"
                bg={token.hex}
                border="2px solid"
                borderColor={activeColor === token.hex ? 'white' : 'transparent'}
                _hover={{ borderColor: 'whiteAlpha.600', transform: 'scale(1.15)' }}
                transition="all 0.1s"
                cursor="pointer"
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault();
                  editor.chain().focus().setColor(token.hex).run();
                  setIsOpen(false);
                }}
              />
            </Tooltip>
          ))}
          {activeColor && (
            <Tooltip label="Clear" placement="bottom" hasArrow openDelay={300}>
              <Box
                gc-testing-id="bubbleMenuColor-clear"
                as="button"
                w="22px"
                h="22px"
                borderRadius="full"
                bg="transparent"
                border="2px solid"
                borderColor="gray.500"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ borderColor: 'red.400', transform: 'scale(1.15)' }}
                transition="all 0.1s"
                cursor="pointer"
                fontSize="10px"
                color="gray.400"
                onMouseDown={(e: React.MouseEvent) => {
                  e.preventDefault();
                  editor.chain().focus().unsetColor().run();
                  setIsOpen(false);
                }}
              >
                ✕
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}

function BubbleToolbar({ editor, singleLine }: { editor: Editor; singleLine: boolean }): React.ReactElement {
  // useEditorState re-renders this component on every editor transaction
  const activeMarks = useEditorState({
    editor,
    selector: (ctx) => ({
      bold: ctx.editor.isActive('bold'),
      italic: ctx.editor.isActive('italic'),
      underline: ctx.editor.isActive('underline'),
      strike: ctx.editor.isActive('strike'),
      link: ctx.editor.isActive('link'),
      textColor: ctx.editor.getAttributes('textStyle')?.color as string | undefined,
      h1: ctx.editor.isActive('heading', { level: 1 }),
      h2: ctx.editor.isActive('heading', { level: 2 }),
      h3: ctx.editor.isActive('heading', { level: 3 }),
      h4: ctx.editor.isActive('heading', { level: 4 }),
    }),
  });

  const handleToggleLink = useCallback(() => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  return (
    <HStack
      gc-testing-id="bubbleMenuToolbar"
      bg="gray.800"
      borderRadius="md"
      px={1}
      py={0.5}
      spacing={0.5}
      boxShadow="lg"
      onMouseDown={(e) => e.preventDefault()}
    >
      <Tooltip label="Bold" placement="top" hasArrow>
        <IconButton
          gc-testing-id="bubbleMenuBold"
          aria-label="Bold"
          icon={<BoldIcon />}
          size="xs"
          variant={activeMarks.bold ? 'solid' : 'ghost'}
          colorScheme={activeMarks.bold ? 'orange' : 'whiteAlpha'}
          color="white"
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
      </Tooltip>
      <Tooltip label="Italic" placement="top" hasArrow>
        <IconButton
          gc-testing-id="bubbleMenuItalic"
          aria-label="Italic"
          icon={<ItalicIcon />}
          size="xs"
          variant={activeMarks.italic ? 'solid' : 'ghost'}
          colorScheme={activeMarks.italic ? 'orange' : 'whiteAlpha'}
          color="white"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
      </Tooltip>
      <Tooltip label="Underline" placement="top" hasArrow>
        <IconButton
          gc-testing-id="bubbleMenuUnderline"
          aria-label="Underline"
          icon={<UnderlineIcon />}
          size="xs"
          variant={activeMarks.underline ? 'solid' : 'ghost'}
          colorScheme={activeMarks.underline ? 'orange' : 'whiteAlpha'}
          color="white"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
      </Tooltip>
      <Tooltip label="Strikethrough" placement="top" hasArrow>
        <IconButton
          gc-testing-id="bubbleMenuStrike"
          aria-label="Strikethrough"
          icon={<StrikeIcon />}
          size="xs"
          variant={activeMarks.strike ? 'solid' : 'ghost'}
          colorScheme={activeMarks.strike ? 'orange' : 'whiteAlpha'}
          color="white"
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
      </Tooltip>
      <Tooltip label="Link" placement="top" hasArrow>
        <IconButton
          gc-testing-id="bubbleMenuLink"
          aria-label="Link"
          icon={<LinkIcon />}
          size="xs"
          variant={activeMarks.link ? 'solid' : 'ghost'}
          colorScheme={activeMarks.link ? 'orange' : 'whiteAlpha'}
          color="white"
          onClick={handleToggleLink}
        />
      </Tooltip>
      <ColorPicker editor={editor} activeColor={activeMarks.textColor} />
      {!singleLine && (
        <>
          {([1, 2, 3, 4] as const).map((level) => (
            <Tooltip key={level} label={`Heading ${level}`} placement="top" hasArrow>
              <IconButton
                gc-testing-id={`bubbleMenuH${level}`}
                aria-label={`Heading ${level}`}
                icon={<HeadingLevelIcon level={level} />}
                size="xs"
                variant={activeMarks[`h${level}` as keyof typeof activeMarks] ? 'solid' : 'ghost'}
                colorScheme={activeMarks[`h${level}` as keyof typeof activeMarks] ? 'orange' : 'whiteAlpha'}
                color="white"
                onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
              />
            </Tooltip>
          ))}
        </>
      )}
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons — lightweight, no external deps
// ---------------------------------------------------------------------------
function BoldIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h8a4 4 0 0 1 2.83 6.83A4 4 0 0 1 15 20H6V4zm3 7h5a1.5 1.5 0 0 0 0-3H9v3zm0 3v3h6a1.5 1.5 0 0 0 0-3H9z" />
    </svg>
  );
}

function ItalicIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 4h4l-2 16H8l2-16z" transform="translate(2,0)" />
    </svg>
  );
}

function UnderlineIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 3v8a6 6 0 0 0 12 0V3h-2.5v8a3.5 3.5 0 0 1-7 0V3H6zM4 21h16v-2H4v2z" />
    </svg>
  );
}

function StrikeIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 12h18v2H3v-2zm5-4h8a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4zm0 8h8a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4z" />
    </svg>
  );
}

function LinkIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7a5 5 0 0 0 0 10h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4a5 5 0 0 0 0-10z" />
    </svg>
  );
}

function ColorIcon({ color }: { color?: string }): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10c.55 0 1-.45 1-1 0-.28-.11-.53-.29-.71-.18-.18-.29-.43-.29-.71 0-.55.45-1 1-1H16c3.31 0 6-2.69 6-6 0-5.52-4.49-9.99-10-9.99z" />
      {color && (
        <rect x="1" y="20" width="22" height="3" rx="1" fill={color} />
      )}
      {!color && (
        <rect x="1" y="20" width="22" height="3" rx="1" fill="currentColor" opacity="0.4" />
      )}
    </svg>
  );
}

function HeadingLevelIcon({ level }: { level: 1 | 2 | 3 | 4 }): React.ReactElement {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>H{level}</span>
  );
}
